import { AppUpdateInfo } from "@capawesome/capacitor-app-update"
import { defineStore } from "pinia"
import { useSalesReportsStore } from "./salesReports"
// import { useCenterReportStore } from "./centerReportStore"
import { Center } from "../user"

type PlateformType = "android" | "ios" | "web"

interface AppSettings {
    taxIncluded: boolean
    experimentalUnlocked: boolean
    experimentalFeatures: boolean
}

interface AppSettingsState {
    centers: Center[]
    name: string
    plateform: PlateformType
    accountFetched: boolean
    dataFetched: boolean
    pageLoadingProgress: number // Percentage of page loading progress
    steps: number,
    barSteps: number,
    updateInfo: AppUpdateInfo | null,
    settings: AppSettings
}

export const useAppSettingsStore = defineStore('app-settings', {
    state: (): AppSettingsState => {
        console.log('DEBUG AppSettingsStore: initializing state')
        return {
        centers: [],
        name: "GoManager",
        plateform: "android",
        accountFetched: false,
        dataFetched: false,
        pageLoadingProgress: 0,
        steps: 0,
        barSteps: 0,
        updateInfo: null,
        settings: {
            taxIncluded: true,
            experimentalUnlocked: false,
            experimentalFeatures: false
        }
        }
    },

    getters: {
        ready(): boolean {
            return this.accountFetched
        },
        getSettings(): AppSettings {
            return this.settings
        },
        mostUsedCurrency(): string {
            console.log('DEBUG mostUsedCurrency: this.centers =', this.centers)
            console.log('DEBUG mostUsedCurrency: this =', this)
            if (!this.centers || this.centers.length === 0) {
                console.log('DEBUG mostUsedCurrency: centers is empty or undefined, returning EUR')
                return 'EUR' // Valeur par défaut
            }
            const currencies = this.centers.map((center) => center.currencyCode)
            const currencyCount: { [key: string]: number } = {}
            currencies.forEach((currency) => {
                if (currencyCount[currency]) {
                    currencyCount[currency]++
                } else {
                    currencyCount[currency] = 1
                }
            })
            return Object.keys(currencyCount).reduce((a, b) => (currencyCount[a] > currencyCount[b] ? a : b))
        },
        mostUsedLanguage(): string {
            console.log('DEBUG mostUsedLanguage: this.centers =', this.centers)
            console.log('DEBUG mostUsedLanguage: this =', this)
            const languages = this.centers.map((center) => center.language)
            const languageCount: { [key: string]: number } = {}
            languages.forEach((language) => {
                if (languageCount[language]) {
                    languageCount[language]++
                } else {
                    languageCount[language] = 1
                }
            })
            return Object.keys(languageCount).reduce((a, b) => (languageCount[a] > languageCount[b] ? a : b))
        },
    },

    actions: {
        setCenters(centers: Center[]) {
            console.log('DEBUG setCenters: received centers =', centers)
            this.centers = centers
            console.log('DEBUG setCenters: this.centers after assignment =', this.centers)
        },
        getCenter(centerId: number): Center | undefined {
            return this.centers.find((center) => center.id === centerId)
        },
        
        setAccountFetched(value: boolean) {
            this.accountFetched = value
        },
        setDataFetched(value: boolean) {
            this.dataFetched = value
        },

        setUpdateInfo(updateInfo: AppUpdateInfo | null) {
            this.updateInfo = updateInfo
        },

        // Set the number of steps to complete in the progress bar
        advance(steps: number = 1) {
            if (this.barSteps == 0) return
            this.steps += steps
            const range = 100 / this.barSteps
            this.setPageLoadingProgress(range * (this.steps))
        },

        // Start the progress bar with the expected number of steps
        // as an async to allow the progress bar to be fluid
        async startLoadingPage(expectedSteps: number = 1): Promise<void> {
            if (this.barSteps > 0) return await this.finishLoadingPage()
            this.setPageLoadingProgress(1)
            return new Promise((resolve) => {
                this.barSteps = expectedSteps
                setTimeout(() => {
                    resolve()
                }, 10)
            })
        },

        async finishLoadingPage(): Promise<void> {
            if (this.barSteps == 0) return

            // Finish the progress bar
            this.setPageLoadingProgress(100)
            return new Promise((resolve) => {
                // Reset the progress bar after a delay of 600ms
                // to allow the user to see the progress bar at 100%
                setTimeout(async () => {
                    this.setPageLoadingProgress(0)
                    this.steps = 0
                    this.barSteps = 0
                    resolve()
                }, 600)
            })
        },

        async setPageLoadingProgress(value: number): Promise<void> {
            return new Promise((resolve) => {
                this.pageLoadingProgress = value
                resolve();
            })
        },

        setPlatform(plateform: PlateformType) {
            this.plateform = plateform
        },

        loadSettings() {
            const settings = localStorage.getItem('app-settings')
            if (settings) {
                this.settings = JSON.parse(settings)
            }
        },

        updateSettings(settings: AppSettings) {
            this.settings = settings
            localStorage.setItem('app-settings', JSON.stringify(this.settings))
        },

        setSetting(setting: keyof AppSettings, value: any) {
            const salesReporsStore = useSalesReportsStore();
            // const centerReportStore = useCenterReportStore();

            this.settings[setting] = value
            localStorage.setItem('app-settings', JSON.stringify(this.settings))

            salesReporsStore.updateTaxDisplay();
            // centerReportStore.refreshFamilies();
        }
    }
})